import { Injectable } from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, map, switchMap, take } from "rxjs/operators";
import { SocialTabService } from "src/app/messenger/pages/social-tabs/social-tab.service";
import { environment } from "src/environments/environment";
import * as socialActions from "./social.actions";
import { SocialData, SocialVideos } from "./social.state";
import moment from 'moment';
import { Observable, of } from "rxjs";
import { HttpErrorResponse } from "@angular/common/http";

@Injectable()
export class SocialEffects {
    constructor(
        private actions$: Actions,
        private socialService: SocialTabService,
        private sanitizer: DomSanitizer,
      ) {}

    loadSocialData$ = createEffect(() => 
        this.actions$.pipe(
            ofType(socialActions.loadSocialData),
            switchMap(() => 
                this.getData().pipe(
                    map((socialData) => {
                        return socialActions.socialDataLoaded(socialData);
                    }),
                    catchError(error => of(socialActions.loadSocialError({errorMessage: this.getErrorMessage(error)}))),
                )
            )
        )
    );

    socialDataLoaded$ = createEffect(() => 
        this.actions$.pipe(
            ofType(socialActions.socialDataLoaded, socialActions.loadSocialError),
        ),
        {
            dispatch: false
        }
    );

    private getData(): Observable<SocialData> {
        return this.socialService.getDataApi().pipe(
            take(1),
            map(data => {
                const socialData: SocialData =  {
                    videos: this.processVideos(data['video']),
                    posts: this.processStories(data['post']),
                    accounts: this.processAccounts(data['account']),
                    podcasts: this.processPodcasts(data['podcast']),
                };
                return socialData;
            })
        )
    }
    
    private processStories(postData: any[]) {
        return postData.reverse().map(post => {
            return {
                ...post,
                author_image: environment.cmsUrl.replace("/api/", post.author_image),
                posted: this.dateFormatter(post.posted_date)
            }
        });
    }

    private processVideos(videos: any[]): SocialVideos {
        const twitchLink = window.location.hostname;
        const liveVideos: any[] = [];
        const trendingVideos: any[] = [];
        const recommendedVideos: any[] = [];
        for (const video of videos) {
            video.duration = this.calculateDuration(video.duration,video.type,video.is_live);
            video.view_count = this.shortenViewNumber(video.view_count);
            video.published_date = this.sincePublishedDate(video.published_date);
            if (
                video.type == "twitch"
                && video.is_live == 0
                && video.content.search("channel") > -1
            ) {
                continue;
            }
            if (video.type == "twitch") {
                video.content = video.content.replace("environmentLink",twitchLink);
            }
            video.content = this.sanitizer.bypassSecurityTrustHtml(video.content);
            if (video.is_live == 1) {
                liveVideos.push(video);
            } else {
                if (video.video_type === 'recommended') {
                    recommendedVideos.push(video);
                } else trendingVideos.push(video);
            }
        }
        return {liveVideos, trendingVideos, recommendedVideos};
    }

    private processAccounts(accounts: any[]) {
        return accounts.map(account => {
            const mappedAccount = {...account};
            const instagramI = mappedAccount.urls.findIndex(x => x.type == 'instagram');
            const youtubeI = mappedAccount.urls.findIndex(x => x.type == 'youtube');
            const twitterI = mappedAccount.urls.findIndex(x => x.type == 'twitter');
            const twitchI = mappedAccount.urls.findIndex(x => x.type == 'twitch');
            if (instagramI > -1) mappedAccount.instagramUrl = mappedAccount.urls[instagramI].url;
            if (youtubeI > -1) mappedAccount.youtubeUrl = mappedAccount.urls[youtubeI].url;
            if (twitterI > -1) mappedAccount.twitterUrl = mappedAccount.urls[twitterI].url;
            if (twitchI > -1) mappedAccount.twitchUrl = mappedAccount.urls[twitchI].url;
            return mappedAccount;
        });
    }

    private processPodcasts(podcasts: any[]) {
        return podcasts.map(podcast => {
            const mappedPodcast = {...podcast};
            const appleIndex = mappedPodcast.urls.findIndex(x => x.type == 'apple');
            const spotifyIndex = mappedPodcast.urls.findIndex(x => x.type == 'spotify');
            if (appleIndex > -1) mappedPodcast.appleUrl = mappedPodcast.urls[appleIndex].url;
            if (spotifyIndex > -1) mappedPodcast.spotifyUrl = mappedPodcast.urls[spotifyIndex].url;
            return mappedPodcast;
        });
    }

    private dateFormatter(date) {
        const dateSplit = date.split("/");
        const month= ["January","February","March","April","May","June","July",
            "August","September","October","November","December"];
        const formattedDate = month[parseInt(dateSplit[0]) - 1] + " " + dateSplit[1] + ", 20" + dateSplit[2];

        return formattedDate;
    }

    private calculateDuration(duration, type, live) {
        const durationInMoment = moment.duration(duration);
        let hours = 0;
        let minutes = 0;
        let seconds = 0;
        let returnValue = "";
        if (live < 1) {
            if (type == "youtube") {
                hours = Math.floor(durationInMoment.asHours()) % 60;
                minutes = Math.floor(durationInMoment.asMinutes()) % 60;
                seconds = Math.floor(durationInMoment.asSeconds()) % 60;
            } else {
                hours = duration.match(/([\d.]+) *h/)[1];
                minutes = duration.match(/([\d.]+) *m/)[1];
                seconds = duration.match(/([\d.]+) *s/)[1];
            }
        } else return "";
        if (hours > 0) returnValue = hours + ":";
        if (minutes > 0) returnValue = returnValue + minutes + ":";
        else returnValue = returnValue +"00:";
        if (seconds > 0) returnValue = returnValue + seconds;
        else returnValue = returnValue + "00";
        return returnValue;
    }

    shortenViewNumber(number: number): any {
        if (isNaN(number)) return null;
        if (number === null) return null;
        if (number === 0) return null;
        let abs = Math.abs(number);
        const rounder = Math.pow(10, 1);
        const isNegative = number < 0;
        let key = '';

        const powers = [
            {key: 'Q', value: Math.pow(10, 15)},
            {key: 'T', value: Math.pow(10, 12)},
            {key: 'B', value: Math.pow(10, 9)},
            {key: 'M', value: Math.pow(10, 6)},
            {key: 'K', value: 1000}
        ];

        for (let i = 0; i < powers.length; i++) {
            let reduced = abs / powers[i].value;
            reduced = Math.round(reduced * rounder) / rounder;
            if (reduced >= 1) {
                abs = reduced;
                key = powers[i].key;
                break;
            }
        }
        return (isNegative ? '-' : '') + abs + key;
    }

    sincePublishedDate(value: any) {
        if (value) {
            const seconds = Math.floor((+new Date() - +new Date(value)) / 1000);
            if (seconds < 29) // less than 30 seconds ago will show as 'Just now'
                return 'Just now';
            const intervals = {
                'year': 31536000,
                'month': 2592000,
                'week': 604800,
                'day': 86400,
                'hour': 3600,
                'minute': 60,
                'second': 1
            };
            let counter: number;
            for (const i in intervals) {
                counter = Math.floor(seconds / intervals[i]);
                if (counter > 0)
                    if (counter === 1) {
                        return counter + ' ' + i + ' ago'; // singular (1 day ago)
                    } else {
                        return counter + ' ' + i + 's ago'; // plural (2 days ago)
                    }
            }
        }
        return value;
    }

    private getErrorMessage(error: HttpErrorResponse | TypeError) {
        console.error(error.message);
        return error.message;
    }
}